import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { getLeft, getScrollLeft, getWidth } from './utils';
import { usePropsValue } from '@/utils/use-props-value';
import { withNativeProps } from '@/utils/with-native-props';
import { traverseReactNode } from '@/utils/traverse-react-node';
import { CnTabContext } from './context';
import { useGuid } from '@/utils/use-guid';
import { CnTabItem } from './item';
import './tab.scss';
// get initialized activekey
const getInitActiveKey = (props, activeKey, defaultActiveKey) => {
    if ('activeKey' in props && activeKey) {
        return activeKey;
    }
    else if (defaultActiveKey || defaultActiveKey === 0) {
        return defaultActiveKey;
    }
    else {
        // @ts-ignore
        return undefined;
    }
};
const timerMap = {};
export const CnTab = (props) => {
    const { type, dataSource = [], className, contentClassName, contentStyle, renderTab, additionSlot, tabAlign, extra, children, } = props;
    // 从 children 和 dataSource 获取的第一个 childKey
    let firstActiveKey = null;
    if (props.children) {
        traverseReactNode(props.children, (child, index) => {
            if (!React.isValidElement(child))
                return;
            const { key } = child;
            if (index === 0) {
                firstActiveKey = key;
            }
        });
    }
    else if (Array.isArray(dataSource) && dataSource.length > 0) {
        dataSource.forEach((item, index) => {
            if (index === 0) {
                firstActiveKey = item.key || index;
            }
        });
    }
    // 当前激活 key
    const [curActiveKey, setActiveKey] = usePropsValue({
        value: props.activeKey,
        defaultValue: props.defaultActiveKey ?? firstActiveKey,
        onChange: (v) => {
            if (v === null)
                return;
            props.onChange?.(v);
        },
    });
    // 是否为 overflow
    const [isOverflow, setOverflow] = useState(false);
    // 用于生成唯一的各种 id
    const curTabScrollId = useGuid('tab-scroll-');
    const curScrollContainerId = useGuid('tab-ctn-');
    const scrollMoreLeftId = useGuid('tab-scroll-more-left-');
    const scrollMoreRightId = useGuid('tab-scroll-more-right-');
    const extraId = useGuid('tab-extra-');
    // 滚动容器 ref
    const scrollRef = useRef();
    const activeTabRef = useRef();
    const isControlled = 'activeKey' in props;
    const clsPrefix = 'cn-ui-m-tab';
    const classes = {
        tabWrapper: classNames(`${clsPrefix}-wrapper`, `${clsPrefix}-wrapper-type-${type}`),
        tab: classNames(clsPrefix, className, `${clsPrefix}-type-${type}`),
        tabHeader: `${clsPrefix}-header`,
        divider: `${clsPrefix}-divider`,
        content: classNames(`${clsPrefix}-content`, `${clsPrefix}-content-type-${type}`, contentClassName),
        container: classNames(`${clsPrefix}-container`, {
            [`${clsPrefix}-container-overflow`]: isOverflow,
        }),
        scrollContainer: classNames(`${clsPrefix}-scroll-container`, {
            [`${clsPrefix}-scroll-container-overflow`]: isOverflow,
            [`${clsPrefix}-scroll-container-align-left`]: tabAlign === 'left',
        }),
    };
    const scrollTo = (offset) => {
        if (!timerMap[curTabScrollId]) {
            timerMap[curTabScrollId] = setTimeout(() => {
                if (scrollRef && scrollRef.current && scrollRef.current.scrollTo) {
                    scrollRef.current.scrollTo({
                        left: offset.x,
                        top: offset.y,
                        duration: 150,
                    });
                    timerMap[curTabScrollId] = null;
                }
            }, 200);
        }
    };
    const onScrollTrigger = () => {
        const scrollLeft = getScrollLeft(curTabScrollId);
        const containerWidth = getWidth(curTabScrollId);
        const extraWidth = getWidth(extraId);
        const totalWidth = document.getElementById(curScrollContainerId)?.scrollWidth || 0;
        const leftMoreEle = document.getElementById(scrollMoreLeftId);
        const rightMoreEle = document.getElementById(scrollMoreRightId);
        if (leftMoreEle && rightMoreEle) {
            if (scrollLeft > 0) {
                leftMoreEle.style.visibility = 'visible';
            }
            else {
                leftMoreEle.style.visibility = 'hidden';
            }
            if (totalWidth > containerWidth) {
                if (Math.floor(scrollLeft) !== Math.floor(totalWidth - containerWidth)) {
                    rightMoreEle.style.right = `${extraWidth}px`;
                    rightMoreEle.style.visibility = 'visible';
                }
                else {
                    rightMoreEle.style.visibility = 'hidden';
                }
            }
        }
    };
    useEffect(() => {
        // reset curActiveKey if new activeKey is invalid
        if (isControlled && curActiveKey && !props.activeKey) {
            setActiveKey(getInitActiveKey(props));
            return;
        }
        if (isControlled) {
            setActiveKey(props.activeKey);
        }
        // 初始根据元素判断是滚动模式
        (async () => {
            const totalWidth = document.getElementById(curScrollContainerId)?.scrollWidth || 0;
            const containerWidth = getWidth(curTabScrollId);
            setOverflow(totalWidth > containerWidth);
            if (totalWidth > containerWidth) {
                onScrollTrigger();
            }
        })();
    });
    // 监听滚动容器设定滚动遮罩的显示
    useEffect(() => {
        const ele = document.getElementById(curTabScrollId);
        ele?.addEventListener('scroll', onScrollTrigger);
    }, []);
    // 根据 activeKey 定位元素
    useEffect(() => {
        (async () => {
            // @ts-ignore
            const activeTabId = activeTabRef.current && activeTabRef.current.id;
            const containerWidth = getWidth(curTabScrollId);
            const containerOffsetLeft = getLeft(curTabScrollId);
            const scrollLeft = getScrollLeft(curTabScrollId);
            const activeTabWidth = getWidth(activeTabId);
            const activeTabOffsetLeft = getLeft(activeTabId) - containerOffsetLeft;
            const centerMarkerPos = containerWidth / 2;
            if (scrollRef.current && activeTabRef.current) {
                const distance = Math.floor(Math.max(activeTabOffsetLeft -
                    centerMarkerPos +
                    scrollLeft +
                    activeTabWidth / 2, 0));
                if (scrollLeft !== distance) {
                    scrollTo({
                        x: distance,
                        y: 0,
                    });
                }
            }
        })();
    }, [curActiveKey]);
    // children 拼装
    let content;
    if (dataSource && dataSource.length > 0) {
        content = dataSource.map((item, index) => {
            const itemProps = {
                key: item.key || index,
                itemKey: item.key,
                renderContent: renderTab,
                type,
                ...item,
            };
            if (item.key === curActiveKey) {
                itemProps.ref = (ref) => {
                    activeTabRef.current = ref;
                };
            }
            return React.createElement(CnTabItem, { ...itemProps });
        });
    }
    else {
        content = React.Children.map(children, (child, index) => {
            return React.cloneElement(child, {
                key: `mt_tab_${index}`,
                itemKey: child.key || index,
                ref: `${child.key}` === `${curActiveKey}` ? activeTabRef : null,
                renderContent: child.renderContent || renderTab,
                type,
            });
        });
    }
    const getChildren = (activeTabKey) => {
        let ret = null;
        React.Children.forEach(children, (child) => {
            if (ret) {
                return;
            }
            const _key = child.key;
            if (`${_key}` === `${activeTabKey}`) {
                ret = child.props.children;
            }
        });
        return ret;
    };
    return (React.createElement(CnTabContext.Provider, { value: {
            // @ts-ignore
            activeKey: curActiveKey,
            change: setActiveKey,
        } },
        React.createElement("div", { className: classes.tabWrapper },
            withNativeProps(props, React.createElement("div", { className: classes.tab },
                React.createElement("div", { className: classes.tabHeader },
                    React.createElement("div", { id: scrollMoreLeftId, className: classNames({
                            [`${clsPrefix}-more`]: true,
                            [`${clsPrefix}-more-left`]: true,
                        }) }),
                    React.createElement("div", { ref: scrollRef, id: curTabScrollId, className: classes.container },
                        React.createElement("div", { id: curScrollContainerId, className: classes.scrollContainer }, content)),
                    React.createElement("div", { id: scrollMoreRightId, className: classNames({
                            [`${clsPrefix}-more`]: true,
                            [`${clsPrefix}-more-right`]: true,
                        }) }),
                    extra && (React.createElement("div", { className: `${clsPrefix}-extra`, id: extraId }, extra))),
                React.createElement("div", { className: classes.divider }),
                additionSlot && React.createElement("div", null, additionSlot))),
            getChildren(curActiveKey) && (React.createElement("div", { className: classes.content, style: contentStyle }, getChildren(curActiveKey))))));
};
CnTab.displayName = 'CnTab';
CnTab.defaultProps = {
    type: 'primary',
    tabAlign: 'center',
};
CnTab.Item = CnTabItem;
